/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import {
  ORG_THEME_STORAGE_KEY,
  ORG_THEME_VARS_STORAGE_KEY,
  THEME_VAR_KEYS,
} from "../constants";
import type { CachedOrgTheme } from "../types";
import { hasThemeStyleTag, removeThemeStyleTag } from "./theme-style-tag";

export function hasInlineThemeVars(): boolean {
  return hasThemeStyleTag();
}

export function readCachedOrgTheme(): CachedOrgTheme | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(ORG_THEME_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedOrgTheme;
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (error) {
    return null;
  }
}

export function writeCachedOrgTheme(payload: CachedOrgTheme): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(ORG_THEME_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    // Silently ignore storage errors
  }
}

export function clearCachedOrgTheme(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(ORG_THEME_STORAGE_KEY);
    localStorage.removeItem(ORG_THEME_VARS_STORAGE_KEY);
  } catch (error) {
    // Silently ignore storage errors
  }
}

/**
 * Remove all org theme CSS variables from the DOM.
 * Called on sign-out so marketing/landing pages don't inherit stale colors.
 */
export function clearThemeFromDOM(): void {
  if (typeof window === "undefined") return;
  removeThemeStyleTag();
}

export function writeCachedThemeVars(isDark: boolean): void {
  if (typeof window === "undefined") return;
  try {
    const computedStyle = getComputedStyle(document.documentElement);
    const vars: Record<string, string> = {};
    THEME_VAR_KEYS.forEach((key) => {
      const value = computedStyle.getPropertyValue(key);
      if (value) {
        vars[key] = value.trim();
      }
    });
    if (Object.keys(vars).length === 0) return;

    const raw = localStorage.getItem(ORG_THEME_VARS_STORAGE_KEY);
    const existing =
      raw && typeof raw === "string"
        ? (JSON.parse(raw) as Record<string, unknown>)
        : {};
    const payload = {
      ...(existing || {}),
      [isDark ? "dark" : "light"]: vars,
      updatedAt: Date.now(),
    };
    localStorage.setItem(ORG_THEME_VARS_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    // Silently ignore storage errors
  }
}
